import React, { useState, useRef, useEffect } from 'react'
import { Box, Button, Layer, Heading, TextInput } from 'grommet'
import { Close, Chat, LinkUp, NewWindow } from 'grommet-icons'
import { MessageBox } from 'react-chat-elements'
import Linkify from 'linkify-react'
import 'react-chat-elements/dist/main.css'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getAuth } from 'firebase/auth'
import { I18n } from 'galarm-config'
import GlobalConfig from '../GlobalConfig'

const DocChatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const messagesContainerRef = useRef(null)
  const conversationIdRef = useRef(null)
  const streamingMessageIndexRef = useRef(null)

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    // Use setTimeout to ensure DOM is updated before scrolling
    const timer = setTimeout(() => {
      scrollToBottom()
    }, 100)
    return () => clearTimeout(timer)
  }, [messages, isLoading, isStreaming])

  const initializeConversation = async () => {
    setMessages([
      {
        position: 'left',
        type: 'text',
        text: I18n.t('docChatbotWelcome'),
        date: new Date()
      }
    ])

    try {
      const result = await startNewConversation('')
      if (result.conversationId) {
        conversationIdRef.current = result.conversationId
      }
    } catch (error) {
      console.error('Error initializing conversation:', error)
    }
  }

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      initializeConversation()
    }
  }, [isOpen])

  const startNewConversation = async userMessage => {
    try {
      const functions = getFunctions()
      const docBotNewConversation = httpsCallable(
        functions,
        'docBotNewConversation'
      )

      const result = await docBotNewConversation({ query: userMessage })

      return {
        response: result.data?.response || I18n.t('docChatbotDefaultResponse'),
        conversationId: result.data?.conversationId
      }
    } catch (error) {
      console.error('Error starting new conversation:', error)
      throw error
    }
  }

  const queryConversation = async (userMessage, convId) => {
    try {
      const functions = getFunctions()
      const docBotQuery = httpsCallable(functions, 'docBotQuery')

      const result = await docBotQuery({
        conversationId: convId,
        message: userMessage
      })

      return result.data?.response || 'I received your question.'
    } catch (error) {
      console.error('Error querying conversation:', error)
      throw error
    }
  }

  const queryConversationStream = async (userMessage, convId) => {
    try {
      const auth = getAuth()
      const idToken = await auth.currentUser.getIdToken()

      const response = await fetch(GlobalConfig.docBotQueryStreamFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          message: userMessage,
          conversationId: convId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      let conversationIdFromServer = null
      let fullResponse = ''
      let buffer = ''

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6))

            switch (data.type) {
              case 'start':
                conversationIdFromServer = data.conversationId
                break

              case 'chunk':
                fullResponse += data.content
                // Update the message incrementally
                if (streamingMessageIndexRef.current !== null) {
                  setMessages(prev => {
                    const newMessages = [...prev]
                    newMessages[streamingMessageIndexRef.current] = {
                      position: 'left',
                      type: 'text',
                      text: fullResponse,
                      date: new Date()
                    }
                    return newMessages
                  })
                  // Scroll to bottom after updating
                  setTimeout(() => scrollToBottom(), 0)
                }
                break

              case 'done':
                streamingMessageIndexRef.current = null
                return {
                  response: fullResponse,
                  conversationId: conversationIdFromServer
                }

              case 'error':
                console.error('Stream error:', data.error)
                throw new Error(data.error)
            }
          }
        }
      }

      return {
        response: fullResponse,
        conversationId: conversationIdFromServer
      }
    } catch (error) {
      console.error('Error streaming conversation:', error)
      throw error
    }
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return

    const userMessage = inputMessage.trim()
    setInputMessage('')

    setMessages(prev => [
      ...prev,
      {
        position: 'right',
        type: 'text',
        text: userMessage,
        date: new Date()
      }
    ])
    setIsLoading(true)

    try {
      let botResponse

      if (!conversationIdRef.current) {
        const result = await startNewConversation(userMessage)
        botResponse = result.response
        if (result.conversationId) {
          conversationIdRef.current = result.conversationId
        }
      } else {
        botResponse = await queryConversation(
          userMessage,
          conversationIdRef.current
        )
      }

      setMessages(prev => [
        ...prev,
        {
          position: 'left',
          type: 'text',
          text: botResponse,
          date: new Date()
        }
      ])
    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          position: 'left',
          type: 'text',
          text: I18n.t('docChatbotError'),
          date: new Date()
        }
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendMessageStream = async () => {
    if (!inputMessage.trim()) return

    const userMessage = inputMessage.trim()
    setInputMessage('')

    setMessages(prev => [
      ...prev,
      {
        position: 'right',
        type: 'text',
        text: userMessage,
        date: new Date()
      }
    ])
    setIsLoading(true)

    try {
      if (!conversationIdRef.current) {
        const result = await startNewConversation(userMessage)
        const botResponse = result.response
        if (result.conversationId) {
          conversationIdRef.current = result.conversationId
        }

        setMessages(prev => [
          ...prev,
          {
            position: 'left',
            type: 'text',
            text: botResponse,
            date: new Date()
          }
        ])
      } else {
        // Add placeholder message for streaming
        setIsStreaming(true)
        setMessages(prev => [
          ...prev,
          {
            position: 'left',
            type: 'text',
            text: '●●●',
            date: new Date()
          }
        ])

        // Set the index of the message being streamed
        streamingMessageIndexRef.current = messages.length + 1

        const result = await queryConversationStream(
          userMessage,
          conversationIdRef.current
        )

        if (result.conversationId && !conversationIdRef.current) {
          conversationIdRef.current = result.conversationId
        }
      }
    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          position: 'left',
          type: 'text',
          text: I18n.t('docChatbotError'),
          date: new Date()
        }
      ])
    } finally {
      setIsLoading(false)
      setIsStreaming(false)
      streamingMessageIndexRef.current = null
    }
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessageStream()
    }
  }

  const handleNewConversation = () => {
    conversationIdRef.current = null
    setInputMessage('')
    initializeConversation()
  }

  if (!isOpen) return null

  return (
    <Layer
      position="bottom-right"
      modal={false}
      margin={{ bottom: 'medium', right: 'medium' }}
      onEsc={onClose}
      responsive={false}
      style={{
        width: '400px',
        maxWidth: '90vw',
        height: '600px',
        maxHeight: '80vh'
      }}>
      <Box
        fill
        elevation="large"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          direction="row"
          align="center"
          justify="between"
          pad="small"
          background="brand"
          flex={false}>
          <Box direction="row" align="center" gap="small">
            <Chat color="white" />
            <Heading level="4" margin="none" color="white">
              {I18n.t('docChatbotTitle')}
            </Heading>
          </Box>
          <Box direction="row" align="center" gap="small">
            <Button
              icon={<NewWindow size="medium" />}
              onClick={handleNewConversation}
              plain
              style={{ color: 'white' }}
            />
            <Button
              icon={<Close size="medium" />}
              onClick={onClose}
              plain
              style={{ color: 'white' }}
            />
          </Box>
        </Box>

        {/* Messages Container */}
        <Box
          ref={messagesContainerRef}
          flex={{ grow: 1, shrink: 1 }}
          overflow={{ vertical: 'auto' }}
          pad="small"
          background="light-2"
          style={{
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          {messages.map((msg, index) => (
            <Box key={index} margin={{ bottom: 'small' }} flex={false}>
              <MessageBox
                position={msg.position}
                type={msg.type}
                text={
                  <Linkify
                    options={{
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    }}>
                    {msg.text}
                  </Linkify>
                }
                date={msg.date}
                styles={{
                  maxWidth: '80%'
                }}
              />
            </Box>
          ))}
          {isLoading && !isStreaming && (
            <Box margin={{ bottom: 'small' }} flex={false}>
              <MessageBox
                position="left"
                type="text"
                text="Typing..."
                date={new Date()}
                styles={{
                  maxWidth: '80%'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Input Area */}
        <Box
          direction="row"
          align="center"
          pad="small"
          gap="small"
          border={{ side: 'top', color: 'light-4' }}
          background="white"
          flex={false}>
          <TextInput
            placeholder={I18n.t('docChatbotPlaceholder')}
            value={inputMessage}
            onChange={e => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            style={{ flex: 1 }}
          />
          <Button
            icon={<LinkUp size="medium" color="white" />}
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            primary
            color="brand"
          />
        </Box>
      </Box>
    </Layer>
  )
}

export default DocChatbot
